import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  OnDestroy,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import type {
  OutlookMailboxOption,
  OutlookMailProcessingResponse,
  OutlookUnsubscribeResponse,
  OutlookWebhookStatusResponse,
} from '../models/outlook-mailbox.model';
import { HolliSessionService } from '../services/holli-session.service';
import { MailService, type DebugOfficeMailSnapshot } from '../services/mail.service';
import { htmlToPlainText } from '../utils/html-to-plain-text';
import { OfficeAuthService } from '../services/office-auth.service';
import { OutlookMailboxService } from '../services/outlook-mailbox.service';

export type HolliGateState =
  | 'holli_loading'
  | 'sso_error'
  | 'tenant_denied'
  | 'session_error'
  | 'mailbox_setup'
  | 'ready';

@Component({
  selector: 'app-taskpane',
  imports: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './taskpane.component.html',
  styleUrl: './taskpane.component.scss',
})
export class TaskpaneComponent implements OnInit, OnDestroy {
  private readonly mailService = inject(MailService);
  private readonly officeAuthService = inject(OfficeAuthService);
  private readonly holliSession = inject(HolliSessionService);
  private readonly outlookMailbox = inject(OutlookMailboxService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  private refreshRunning = false;
  private refreshQueued = false;
  private mailboxItemChangedRegistered = false;
  private unregisterMailboxItemChanged: (() => Promise<void>) | null = null;
  private pollTimer: ReturnType<typeof setInterval> | null = null;
  private trackedMailInternetId = '';
  private readonly pollIntervalMs = 3000;

  readonly loading = signal(true);
  readonly officeHostReady = signal(false);
  readonly holliGate = signal<HolliGateState>('holli_loading');
  readonly holliSessionDetail = signal('');
  readonly mailboxSetupLoading = signal(false);
  readonly mailboxSetupSaving = signal(false);
  readonly mailboxSetupError = signal('');
  readonly mailboxesForSetup = signal<OutlookMailboxOption[]>([]);
  readonly mailboxSelection = signal<Record<string, boolean>>({});
  readonly officeSnapshot = signal<DebugOfficeMailSnapshot | null>(null);
  readonly officeSectionError = signal('');
  readonly officeSectionWarnings = signal<string[]>([]);
  readonly ssoToken = signal('');
  readonly ssoSectionError = signal('');
  readonly replyActionError = signal('');
  readonly actionFeedback = signal('');
  readonly settingsPanelOpen = signal(false);
  readonly settingsFeedback = signal('');

  readonly webhookStatusLoading = signal(false);
  readonly webhookStatusError = signal('');
  readonly webhookStatus = signal<OutlookWebhookStatusResponse | null>(null);
  readonly currentMailProcessing = signal<OutlookMailProcessingResponse | null>(null);
  readonly currentMailProcessingLoading = signal(false);
  readonly currentMailProcessingError = signal('');
  readonly unsubscribeLoading = signal(false);
  readonly logoutLoading = signal(false);

  /** Original HTML from Holli API; used when opening the Outlook reply draft. */
  readonly proposedAnswerHtml = signal('');

  readonly settingsUserEmail = computed(
    () => this.officeSnapshot()?.userEmail?.trim() ?? '',
  );

  readonly settingsHolliLoggedIn = computed(() => this.holliSession.hasValidPassportToken());

  readonly settingsWebhookLabel = computed(() => {
    const webhook = this.webhookStatus();
    if (webhook === null) {
      return 'Onbekend';
    }
    if (!webhook.registered) {
      return 'Niet gekoppeld';
    }
    if (webhook.counts.error > 0) {
      return 'Fout';
    }
    if (webhook.counts.active > 0) {
      return 'Actief';
    }
    if (webhook.counts.pending > 0) {
      return 'Wordt ingesteld';
    }
    return 'Niet actief';
  });

  readonly settingsWebhookIsHealthy = computed(() => {
    const webhook = this.webhookStatus();
    return webhook !== null && webhook.registered && webhook.counts.active > 0 && webhook.counts.error === 0;
  });

  readonly settingsWebhookIsError = computed(() => {
    const webhook = this.webhookStatus();
    return webhook !== null && (webhook.counts.error > 0 || (!webhook.registered && webhook.counts.enabled_total > 0));
  });

  readonly statusMailSelected = computed(
    () => (this.officeSnapshot()?.internetMessageId ?? '').trim() !== '',
  );

  readonly mailStripSender = computed(() => {
    const fromApi = this.currentMailProcessing()?.mail?.sender?.trim();
    if (fromApi) {
      return fromApi;
    }
    return this.officeSnapshot()?.fromEmail?.trim() ?? '';
  });

  readonly mailStripSubject = computed(() => {
    const fromApi = this.currentMailProcessing()?.mail?.subject?.trim();
    if (fromApi) {
      return fromApi;
    }
    return this.officeSnapshot()?.subject?.trim() ?? '';
  });

  readonly proposedAnswerPlain = computed(() => {
    const html = this.proposedAnswerHtml().trim();
    return html !== '' ? htmlToPlainText(html) : '';
  });

  readonly mainStatusLabel = computed(() => {
    const status = this.currentMailProcessing()?.status ?? 'not_received';
    switch (status) {
      case 'ready':
      case 'mock_answer_ready':
        return 'Antwoord klaar';
      case 'processing':
        return 'Antwoord wordt gemaakt';
      case 'received':
        return 'Bericht ontvangen';
      case 'error':
        return 'Fout bij verwerken';
      case 'rejected':
        return 'Antwoord afgewezen';
      default:
        return 'Wachten op webhook';
    }
  });

  readonly mainStatusIsSuccess = computed(() => {
    const processing = this.currentMailProcessing();
    if (processing === null || !processing.found) {
      return false;
    }
    const status = processing.status;
    if (status !== 'ready' && status !== 'mock_answer_ready') {
      return false;
    }
    return (processing.mail?.generated_answer?.trim() ?? '') !== '';
  });

  readonly mainStatusIsPending = computed(() => {
    const status = this.currentMailProcessing()?.status ?? 'not_received';
    return status === 'not_received' || status === 'received' || status === 'processing';
  });

  readonly mainStatusIsError = computed(() => {
    if (this.currentMailProcessingError() !== '') {
      return true;
    }
    const status = this.currentMailProcessing()?.status;
    return status === 'error';
  });

  readonly statusErrorMessage = computed(() => {
    if (this.currentMailProcessingError() !== '') {
      return this.currentMailProcessingError();
    }
    const processing = this.currentMailProcessing();
    if (processing?.status === 'error') {
      const backendError = processing.mail?.error?.trim() ?? '';
      if (backendError !== '') {
        return backendError;
      }
      return 'Verwerken is mislukt. Tik op Vernieuwen om opnieuw te proberen.';
    }
    return '';
  });

  readonly isHolliBusy = computed(() => {
    if (this.loading()) {
      return true;
    }
    if (this.mainStatusIsError()) {
      return false;
    }
    return this.mainStatusIsPending();
  });

  readonly holliBusyMessage = computed(() => {
    const status = this.currentMailProcessing()?.status ?? 'not_received';
    switch (status) {
      case 'received':
        return 'Bericht ontvangen';
      case 'processing':
        return 'Antwoord wordt gemaakt';
      default:
        return 'Wachten op webhook';
    }
  });

  readonly canShowProposedAnswer = computed(() => this.mainStatusIsSuccess());

  ngOnInit(): void {
    void this.bootstrapApplication();
  }

  ngOnDestroy(): void {
    this.stopPolling();
    void this.unregisterMailboxItemChanged?.().then(() => {
      this.unregisterMailboxItemChanged = null;
    });
  }

  @HostListener('document:keydown', ['$event'])
  onDocumentEscape(event: KeyboardEvent): void {
    if (event.key === 'Escape' && this.settingsPanelOpen()) {
      event.preventDefault();
      this.closeSettingsPanel();
    }
  }

  openSettingsPanel(): void {
    this.settingsPanelOpen.set(true);
    this.settingsFeedback.set('');
    if (this.holliGate() === 'ready') {
      void this.refreshWebhookStatus();
    }
  }

  closeSettingsPanel(): void {
    this.settingsPanelOpen.set(false);
  }

  async refreshAll(): Promise<void> {
    if (this.refreshRunning) {
      this.refreshQueued = true;
      return;
    }
    this.refreshRunning = true;
    try {
      do {
        this.refreshQueued = false;
        await this.runRefresh();
      } while (this.refreshQueued);
    } finally {
      this.refreshRunning = false;
    }
  }

  private async bootstrapApplication(): Promise<void> {
    this.holliGate.set('holli_loading');
    this.holliSessionDetail.set('');
    if (typeof Office === 'undefined') {
      this.holliGate.set('sso_error');
      this.holliSessionDetail.set('Office.js is niet geladen (open deze add-in in Outlook).');
      return;
    }
    try {
      await this.mailService.ensureOfficeReady();
      this.officeHostReady.set(true);
      await this.registerMailboxItemChangedIfNeeded();
    } catch (err: unknown) {
      this.holliGate.set('sso_error');
      this.holliSessionDetail.set(
        err instanceof Error ? err.message : 'Office kon niet worden geïnitialiseerd.',
      );
      return;
    }
    try {
      const token = await this.mailService.getGraphSsoToken();
      this.ssoToken.set(token);
    } catch (err: unknown) {
      this.holliGate.set('sso_error');
      this.holliSessionDetail.set(
        err instanceof Error ? err.message : 'SSO-token kon niet worden opgehaald.',
      );
      return;
    }
    await this.resolveHolliSession();
    this.changeDetectorRef.markForCheck();
  }

  private async resolveHolliSession(): Promise<void> {
    const officeJwt = this.ssoToken().trim();
    if (officeJwt === '') {
      this.holliGate.set('sso_error');
      this.holliSessionDetail.set('Geen Office SSO-token.');
      return;
    }

    const loadStatus = async (): Promise<boolean> => {
      const st = await this.outlookMailbox.getOnboardingStatus();
      return st.mailboxes_configured === true;
    };

    if (this.holliSession.hasValidPassportToken()) {
      try {
        if (await loadStatus()) {
          this.holliGate.set('ready');
          await this.runRefreshOfficeContext();
          return;
        }
        await this.enterMailboxSetup(officeJwt);
        return;
      } catch (err: unknown) {
        if (err instanceof HttpErrorResponse && err.status === 401) {
          this.holliSession.clearPassportToken();
        } else {
          this.holliGate.set('session_error');
          this.holliSessionDetail.set(
            err instanceof Error ? err.message : 'Kon de Holli-status niet ophalen.',
          );
          return;
        }
      }
    }

    try {
      const login = await this.officeAuthService.loginWithOfficeToken(officeJwt);
      this.holliSession.setPassportToken(login.access_token);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : '';
      if (
        msg.includes('Holli-klant') ||
        msg.includes('Microsoft-tenant') ||
        msg.toLowerCase().includes('no holli customer')
      ) {
        this.holliGate.set('tenant_denied');
        this.holliSessionDetail.set('');
        return;
      }
      this.holliGate.set('session_error');
      this.holliSessionDetail.set(msg !== '' ? msg : 'Inloggen bij Holli is mislukt.');
      return;
    }

    try {
      if (await loadStatus()) {
        this.holliGate.set('ready');
        await this.runRefreshOfficeContext();
        return;
      }
      await this.enterMailboxSetup(officeJwt);
    } catch (err: unknown) {
      this.holliGate.set('session_error');
      this.holliSessionDetail.set(
        err instanceof Error ? err.message : 'Kon de Holli-status niet ophalen.',
      );
    }
  }

  private async enterMailboxSetup(officeJwt: string): Promise<void> {
    this.holliGate.set('mailbox_setup');
    this.mailboxSetupError.set('');
    this.mailboxesForSetup.set([]);
    this.mailboxSetupLoading.set(true);
    this.changeDetectorRef.markForCheck();
    try {
      const res = await this.outlookMailbox.listMailboxes(officeJwt);
      const list = res.mailboxes;
      this.mailboxesForSetup.set(list);
      const sel: Record<string, boolean> = {};
      for (const m of list) {
        sel[m.graph_user_id] = true;
      }
      this.mailboxSelection.set(sel);
    } catch (err: unknown) {
      this.mailboxSetupError.set(this.outlookMailbox.messageForMailboxHttpError(err));
    } finally {
      this.mailboxSetupLoading.set(false);
      this.changeDetectorRef.markForCheck();
    }
  }

  async retryHolliSession(): Promise<void> {
    this.holliGate.set('holli_loading');
    this.holliSessionDetail.set('');
    try {
      const token = await this.mailService.getGraphSsoToken();
      this.ssoToken.set(token);
    } catch (err: unknown) {
      this.holliGate.set('sso_error');
      this.holliSessionDetail.set(
        err instanceof Error ? err.message : 'SSO-token kon niet worden opgehaald.',
      );
      return;
    }
    await this.resolveHolliSession();
    this.changeDetectorRef.markForCheck();
  }

  async saveMailboxSetup(): Promise<void> {
    const officeJwt = this.ssoToken().trim();
    if (officeJwt === '') {
      this.mailboxSetupError.set('Geen Office-token. Vernieuw het venster.');
      return;
    }
    const selection = this.mailboxSelection();
    const chosen = this.mailboxesForSetup().filter((m) => selection[m.graph_user_id] === true);
    if (chosen.length === 0) {
      this.mailboxSetupError.set('Vink je mailbox aan om door te gaan.');
      return;
    }
    this.mailboxSetupSaving.set(true);
    this.mailboxSetupError.set('');
    try {
      await this.outlookMailbox.activateMailboxes(
        officeJwt,
        chosen.map((m) => ({ graph_user_id: m.graph_user_id, type: 'primary' })),
      );
      const st = await this.outlookMailbox.getOnboardingStatus();
      if (!st.mailboxes_configured) {
        this.mailboxSetupError.set(
          'Activeren is doorgegeven, maar de status is nog niet bijgewerkt. Wacht even en tik op Vernieuwen.',
        );
        return;
      }
      this.holliGate.set('ready');
      await this.runRefreshOfficeContext();
    } catch (err: unknown) {
      this.mailboxSetupError.set(this.outlookMailbox.messageForMailboxHttpError(err));
    } finally {
      this.mailboxSetupSaving.set(false);
      this.changeDetectorRef.markForCheck();
    }
  }

  toggleMailboxSelection(graphUserId: string, checked: boolean): void {
    this.mailboxSelection.update((cur) => ({ ...cur, [graphUserId]: checked }));
  }

  checkboxChecked(event: Event): boolean {
    const t = event.target;
    return t instanceof HTMLInputElement && t.checked;
  }

  private async runRefreshOfficeContext(): Promise<void> {
    this.loading.set(true);
    this.officeSectionError.set('');
    this.officeSectionWarnings.set([]);
    this.ssoSectionError.set('');

    try {
      const { snapshot, sectionWarnings } = await this.mailService.fetchDebugOfficeSnapshot();
      this.officeSnapshot.set(snapshot);
      this.officeSectionWarnings.set(sectionWarnings);
    } catch (err: unknown) {
      this.officeSectionError.set(
        err instanceof Error ? err.message : 'Kon mailboxgegevens niet lezen.',
      );
    }

    try {
      const token = await this.mailService.getGraphSsoToken();
      this.ssoToken.set(token);
    } catch (err: unknown) {
      this.ssoSectionError.set(
        err instanceof Error ? err.message : 'SSO-token kon niet worden opgehaald.',
      );
    }

    await this.refreshWebhookStatus();
    await this.refreshCurrentMailProcessing();

    this.loading.set(false);
    this.changeDetectorRef.markForCheck();
  }

  private async refreshWebhookStatus(): Promise<void> {
    this.webhookStatusLoading.set(true);
    this.webhookStatusError.set('');
    try {
      const status = await this.outlookMailbox.getWebhookStatus();
      this.webhookStatus.set(status);
    } catch (err: unknown) {
      this.webhookStatus.set(null);
      this.webhookStatusError.set(this.outlookMailbox.messageForMailboxHttpError(err));
    } finally {
      this.webhookStatusLoading.set(false);
    }
  }

  private async refreshCurrentMailProcessing(options: { silent?: boolean } = {}): Promise<void> {
    const mailId = this.officeSnapshot()?.internetMessageId?.trim() ?? '';
    if (mailId === '') {
      this.trackedMailInternetId = '';
      this.currentMailProcessing.set(null);
      this.currentMailProcessingError.set('');
      this.applyReplyDraftFromProcessing(null);
      return;
    }

    if (mailId !== this.trackedMailInternetId) {
      this.trackedMailInternetId = mailId;
      this.proposedAnswerHtml.set('');
      this.replyActionError.set('');
      this.actionFeedback.set('');
      this.currentMailProcessingError.set('');
    }

    if (!options.silent) {
      this.currentMailProcessingLoading.set(true);
    }
    if (!options.silent) {
      this.currentMailProcessingError.set('');
    }
    try {
      const processing = await this.outlookMailbox.getCurrentMailProcessingStatus(mailId);
      this.currentMailProcessing.set(processing);
      this.applyReplyDraftFromProcessing(processing);
    } catch (err: unknown) {
      this.currentMailProcessing.set(null);
      this.currentMailProcessingError.set(this.outlookMailbox.messageForMailboxHttpError(err));
      this.stopPolling();
    } finally {
      if (!options.silent) {
        this.currentMailProcessingLoading.set(false);
      }
      this.syncPolling();
    }
  }

  private shouldPollMailProcessing(): boolean {
    if (this.holliGate() !== 'ready') {
      return false;
    }
    if (this.currentMailProcessingError() !== '') {
      return false;
    }
    const mailId = this.officeSnapshot()?.internetMessageId?.trim() ?? '';
    if (mailId === '') {
      return false;
    }
    const status = this.currentMailProcessing()?.status ?? 'not_received';
    if (status === 'error' || status === 'rejected') {
      return false;
    }
    return status === 'not_received' || status === 'received' || status === 'processing';
  }

  private syncPolling(): void {
    if (this.shouldPollMailProcessing()) {
      this.startPolling();
      return;
    }
    this.stopPolling();
  }

  private startPolling(): void {
    if (this.pollTimer !== null) {
      return;
    }
    this.pollTimer = setInterval(() => {
      void this.pollCurrentMailProcessing();
    }, this.pollIntervalMs);
  }

  private stopPolling(): void {
    if (this.pollTimer === null) {
      return;
    }
    clearInterval(this.pollTimer);
    this.pollTimer = null;
  }

  private async pollCurrentMailProcessing(): Promise<void> {
    if (!this.shouldPollMailProcessing()) {
      this.stopPolling();
      return;
    }
    await this.refreshCurrentMailProcessing({ silent: true });
    this.syncPolling();
  }

  private applyReplyDraftFromProcessing(processing: OutlookMailProcessingResponse | null): void {
    if (processing === null || !this.isAnswerReadyStatus(processing.status)) {
      this.proposedAnswerHtml.set('');
      return;
    }

    const answerHtml = processing.mail?.generated_answer?.trim() ?? '';
    this.proposedAnswerHtml.set(answerHtml);
  }

  private isAnswerReadyStatus(status: string): boolean {
    return status === 'ready' || status === 'mock_answer_ready';
  }

  private async runRefresh(): Promise<void> {
    if (this.holliGate() !== 'ready') {
      await this.bootstrapApplication();
      return;
    }
    await this.runRefreshOfficeContext();
  }

  private async registerMailboxItemChangedIfNeeded(): Promise<void> {
    if (this.mailboxItemChangedRegistered) {
      return;
    }
    try {
      this.unregisterMailboxItemChanged = await this.mailService.registerMailboxItemChanged(() => {
        void this.refreshAll();
      });
      this.mailboxItemChangedRegistered = true;
    } catch (err: unknown) {
      console.error('[MailService]', err);
    }
  }

  canCreateReplyDraft(): boolean {
    return (
      this.canShowProposedAnswer() &&
      this.statusMailSelected() &&
      this.proposedAnswerHtml().trim().length > 0
    );
  }

  createReplyDraft(scope: 'reply' | 'reply_all'): void {
    this.replyActionError.set('');
    this.actionFeedback.set('');
    const answerHtml = this.proposedAnswerHtml().trim();
    if (answerHtml === '') {
      this.replyActionError.set('Er is nog geen antwoord beschikbaar.');
      return;
    }
    if (!this.statusMailSelected()) {
      this.replyActionError.set('Selecteer eerst een bericht om te beantwoorden.');
      return;
    }
    try {
      if (scope === 'reply_all') {
        this.mailService.openReplyAllFormFromHtml(answerHtml);
      } else {
        this.mailService.openReplyFormFromHtml(answerHtml);
      }
      this.actionFeedback.set('Antwoord geopend — bewerk en verstuur in Outlook.');
    } catch (err: unknown) {
      this.replyActionError.set(
        err instanceof Error ? err.message : 'Het antwoordvenster kon niet worden geopend.',
      );
    }
  }

  async dismissReply(): Promise<void> {
    const mailId = this.officeSnapshot()?.internetMessageId?.trim() ?? '';
    if (mailId === '') {
      this.replyActionError.set('Geen mail-id gevonden om af te wijzen.');
      return;
    }

    this.replyActionError.set('');
    this.actionFeedback.set('');
    try {
      const result = await this.outlookMailbox.rejectCurrentMail(mailId);
      this.proposedAnswerHtml.set('');
      this.actionFeedback.set(result.message);
      await this.refreshCurrentMailProcessing();
      await this.refreshWebhookStatus();
    } catch (err: unknown) {
      this.replyActionError.set(this.outlookMailbox.messageForMailboxHttpError(err));
    }
  }

  webhookNextExpirationReadable(): string {
    const iso = this.webhookStatus()?.next_expiration_at ?? '';
    if (iso.trim() === '') {
      return '—';
    }

    const timestamp = Date.parse(iso);
    if (Number.isNaN(timestamp)) {
      return iso;
    }

    return new Date(timestamp).toLocaleString('nl-NL');
  }

  async unsubscribeFromOutlook(): Promise<void> {
    this.settingsFeedback.set('');
    this.unsubscribeLoading.set(true);
    try {
      const result: OutlookUnsubscribeResponse = await this.outlookMailbox.unsubscribeSubscriptions();
      this.settingsFeedback.set(result.message);
      this.stopPolling();
      this.holliGate.set('mailbox_setup');
      this.mailboxesForSetup.set([]);
      this.mailboxSelection.set({});
      this.currentMailProcessing.set(null);
      this.webhookStatus.set(null);
      this.closeSettingsPanel();
      const officeJwt = this.ssoToken().trim();
      if (officeJwt !== '') {
        await this.enterMailboxSetup(officeJwt);
      }
    } catch (err: unknown) {
      this.settingsFeedback.set(this.outlookMailbox.messageForMailboxHttpError(err));
    } finally {
      this.unsubscribeLoading.set(false);
      this.changeDetectorRef.markForCheck();
    }
  }

  async logoutFromHolli(): Promise<void> {
    this.settingsFeedback.set('');
    this.logoutLoading.set(true);
    try {
      this.holliSession.clearPassportToken();
      this.stopPolling();
      this.currentMailProcessing.set(null);
      this.webhookStatus.set(null);
      this.proposedAnswerHtml.set('');
      this.closeSettingsPanel();
      await this.bootstrapApplication();
      this.settingsFeedback.set('');
    } catch (err: unknown) {
      this.settingsFeedback.set(
        err instanceof Error ? err.message : 'Uitloggen is mislukt.',
      );
    } finally {
      this.logoutLoading.set(false);
      this.changeDetectorRef.markForCheck();
    }
  }
}
